<script lang="ts">
    import {getLogger} from 'src/core';
    import type {FormControlOptions} from '../../forms/types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {MultipleAcceptableValueEditorLocalizations, MultipleAcceptableValueEditorOptions} from './types';
    import type {ValueEditorValidations} from '../../../kp-value-editor/types';
    import type {MultiSelectEvents, ObjectOption, Option} from 'svelte-multiselect';
    import type {ActionDestroyHandle} from './utils';
    import type {ValueEditorSize} from '../../../types';
    import type {FormControl} from '../../forms/form-control';
    import {FormControlBuilder} from '../../forms/form-control';
    import {emptyAsNullFormatter, emptyAsNullParser} from '../_shared/editors-utils';
    import {createInputDescription} from '../../description/utils';
    import {getContext, onDestroy, onMount, tick} from 'svelte';
    import {isFunction, isNull, isNullOrUndefined, isUndefined} from 'shared/utils/custom-utils';
    import {slide} from 'svelte/transition';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {ariaLabelledby} from '../../label/use.aria-labelledby';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {style} from 'svelte-forms';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {isObject} from 'shared/utils/types-utils';
    import {loc} from 'shared/utils/pipes';
    import {isString} from 'shared/utils/string-utils';
    import MultiSelect from 'svelte-multiselect';
    import {FOCUS_OUTSIDE_EVENT_TYPE, focusOutside} from './use.focus-outside';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import Checkbox from '../../../../ui-widgets/checkbox/Checkbox.svelte';
    import {
        detachActions,
        getComparator,
        getOptionById,
        getOptionDisabledResolver,
        getOptionId,
        getOptionIdResolver,
        identifierToModelParser,
        modelToIdentifierFormatter,
        sortModelParser,
        tryToFindOptionById,
        useAction,
        useActionsOnElement,
        validatorFactory
    } from './utils';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';

    type MODEL_VALUE = $$Generic;

    export let model: MODEL_VALUE[] | null;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: MultipleAcceptableValueEditorOptions<MODEL_VALUE>;
    export let validations: ValueEditorValidations;

    model = normalizeModelIfNeeded(model);

    const localize: EditorsLocalizationFunction<MultipleAcceptableValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const labelContext = getValueEditorLabelContext();

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withOptions(formControlOptions)
        .withValidators([...validatorFactory(validations)])
        .withParsers([identifierToModelParser(() => options.acceptableValues, options), ...options.sortModel ? [sortModelParser(options)] : []])
        .withFormatters([modelToIdentifierFormatter(options), emptyAsNullFormatter(options, [])])
        .withModifiers([emptyAsNullParser(options)])
        .build(getLogger());

    $: model = normalizeModelIfNeeded(model);

    const modelValueSubscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    let currentViewValue: (string | number)[];
    const viewValueSubscription = formControl.getViewValue$().subscribe((value) => currentViewValue = value);

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    let forceBlockMode = false;
    let inlineMode: boolean;
    $: inlineMode = !forceBlockMode && (options.switchToInlineModeThreshold !== 0 && options.acceptableValues.length >= options.switchToInlineModeThreshold);

    let isListboxOpen: boolean;
    type CheckboxComponent = {getInputRef(): HTMLInputElement};
    const checkboxRefs: CheckboxComponent[] = [];
    let checkboxListRef: HTMLUListElement;

    let multiselectInputRef: HTMLInputElement | null;
    let multiselectContainerRef: HTMLDivElement | null;
    let multiselectInputHandlers: ActionDestroyHandle[];
    let multiselectContainerHandlers: ActionDestroyHandle[];

    let displayedOptionsCount: number;
    $: displayedOptionsCount = !hasMoreCheckboxes || isCheckboxListExpanded ? options.acceptableValues.length : options.showFirstCount;

    let selectableOptionsIdentifiers: (string | number)[];
    $: selectableOptionsIdentifiers = createSelectableOptions(options.acceptableValues, displayedOptionsCount, getComparator(options));

    let hasMoreCheckboxes: boolean;
    $: hasMoreCheckboxes = options.showFirstCount && moreCheckboxesCount > 0;
    let moreCheckboxesCount: number;
    $: moreCheckboxesCount = Math.max((options.acceptableValues ?? []).length - options.showFirstCount, 0);

    let isCheckboxListExpanded = false;

    // use actions on multiselect container element
    $: {
        if (!isNullOrUndefined(multiselectContainerRef)) {
            multiselectContainerRef.setAttribute('data-main-input', 'data-main-input');
            if (!!editorId) {
                multiselectContainerRef.setAttribute('data-editor-id', editorId);
            }
            const appliedActions = [
                useAction(focusOutside),
                useAction(touched, {formControl, touchTriggerEvent: FOCUS_OUTSIDE_EVENT_TYPE})];
            multiselectContainerHandlers = useActionsOnElement(multiselectContainerRef, appliedActions, multiselectContainerHandlers);
        } else {
            detachActions(multiselectContainerHandlers);
        }
    }

    // use actions on multiselect input element
    $: {
        if (!isNullOrUndefined(multiselectInputRef)) {
            multiselectInputRef.setAttribute('data-listbox-toggle', 'data-listbox-toggle');
            const appliedActions = [
                useAction(ariaInvalid, formControl),
                useAction(ariaErrormessage, formControl),
                useAction(ariaDescribedby, inputDescription),
                useAction(focusOnMount, isFocused)];
            multiselectInputHandlers = useActionsOnElement(multiselectInputRef, appliedActions, multiselectInputHandlers);
        } else {
            detachActions(multiselectInputHandlers);
        }
    }

    onMount(() => {
        if (isFocused && !inlineMode && checkboxRefs.length > 0 && !isNullOrUndefined(checkboxRefs[0])) {
            checkboxRefs[0].getInputRef().focus();
        }
    });

    onDestroy(() => {
        detachActions(multiselectInputHandlers);
        detachActions(multiselectContainerHandlers);
        modelValueSubscription.unsubscribe();
        viewValueSubscription.unsubscribe();
    });

    function normalizeModelIfNeeded(modelValue: typeof model | null) {
        if (!Array.isArray(modelValue)) {
            if (options.emptyAsNull) {
                modelValue = null;
            } else {
                modelValue = [];
            }
        } else {
            if (options.emptyAsNull && modelValue.length === 0) {
                modelValue = null;
            } else if (isFunction(options.sortComparator) && options.sortModel && !options.reorderable) {
                modelValue = [...modelValue.sort(getComparator(options))];
            }
        }
        return modelValue;
    }

    function createSelectableOptions(acceptableValues: typeof options.acceptableValues,
                                     count: number,
                                     comparator): (string | number)[] {
        const selectableValues = [...acceptableValues];

        if (isFunction(options.sortComparator)) {
            selectableValues.sort(comparator);
        }

        return selectableValues
            .map(mapToOptionIdentifiers)
            .slice(0, count);
    }

    const mapToOptionIdentifiers = (option: MODEL_VALUE) => getOptionId(option, getOptionIdResolver(options));

    function isOptionDisabled(optionIdentifier: string | number): boolean {
        const optionDisabledResolver = getOptionDisabledResolver(options, model);
        const option = getOptionById(addModelValueIfNotInOptions(options.acceptableValues), getOptionIdResolver(options), optionIdentifier);
        return optionDisabledResolver(option);
    }

    function addModelValueIfNotInOptions(acceptableValues: MODEL_VALUE[]): MODEL_VALUE[] {
        const optionIdResolver = getOptionIdResolver(options);
        const missingModelValues = isNull(model) ? [] : model.filter((modelValue) => isUndefined(tryToFindOptionById(acceptableValues, optionIdResolver, getOptionId(modelValue, optionIdResolver))));

        return [...acceptableValues, ...missingModelValues];
    }

    async function toggleExpand() {
        isCheckboxListExpanded = !isCheckboxListExpanded;
        if (isCheckboxListExpanded) {
            await tick();
            // focus first newly added option (to keep focus in the same place before list expansion)
            checkboxRefs[options.showFirstCount].getInputRef().focus();
        }
    }

    function toggleOptionCheckbox(checked: boolean, optionIdentifier: string | number) {
        if (checked) {
            selectOption(optionIdentifier);
        } else {
            deselectOption(optionIdentifier);
        }
    }

    function selectAll() {
        formControl.setViewValue([...getDisabledSelectedOptions(), ...getSelectableOptions()].map(mapToOptionIdentifiers));
        formControl.setTouched();
    }

    function deselectAll() {
        formControl.setViewValue(getDisabledSelectedOptions().map(mapToOptionIdentifiers)); // leave only disabled selected options
        formControl.setTouched();
    }

    let isNoneSelectableOptionSelected: boolean;
    $: {
        isNoneSelectableOptionSelected = isNull(model) || model.length === getDisabledSelectedOptions().length;
    }

    let areAllSelectableOptionSelected: boolean;
    $: {
        areAllSelectableOptionSelected = model?.length === getSelectableOptions().length + getDisabledSelectedOptions().length;
    }

    function getDisabledSelectedOptions() {
        return options.acceptableValues
            .filter((acceptableValue) => model ? tryToFindOptionById(model, getOptionIdResolver(options), getOptionId(acceptableValue, getOptionIdResolver(options))) : false)
            .filter(getOptionDisabledResolver(options, model)) ?? [];
    }

    function getSelectableOptions() {
        const optionDisabledResolver = getOptionDisabledResolver(options, model);
        const notDisabled = (option) => !optionDisabledResolver(option);
        return options.acceptableValues.filter(notDisabled);
    }

    function selectOption(optionIdentifier: string | number) {
        const optionIdentifiers = new Set([...currentViewValue]);
        optionIdentifiers.add(optionIdentifier);
        formControl.setViewValue(Array.from(optionIdentifiers));
    }

    function deselectOption(optionIdentifier: string | number) {
        const optionIdentifiers = new Set([...currentViewValue]);
        optionIdentifiers.delete(optionIdentifier);
        formControl.setViewValue(Array.from(optionIdentifiers));
    }

    function assertObjectOption(svelteMultiSelectOption: Option): ObjectOption {
        if (!isObject(svelteMultiSelectOption)) {
            throw new Error(`svelteMultiSelectOption: ${JSON.stringify(svelteMultiSelectOption)} should be of type ObjectOption`);
        }
        return svelteMultiSelectOption;
    }

    function onAdd(event: MultiSelectEvents['add']) {
        const objectOption = assertObjectOption(event.detail.option);
        if (!objectOption.disabled) {  // sanity check
            selectOption(objectOption.value as string | number);
        }
    }

    function onRemove(event: MultiSelectEvents['remove']) {
        const objectOption = assertObjectOption(event.detail.option);
        if (!objectOption.disabled) {
            deselectOption(objectOption.value as string | number);
        } else {
            formControl.setViewValue(Array.from([...currentViewValue]));
        }
    }

    function addMetadataForMultiSelect(identifiers: (string | number)[], isEditorDisabled: boolean): ObjectOption[] {
        return identifiers.map((identifier, index) => ({
            label: identifier, // used by svelte-multiselect as unique identifier, IT IS NOT USED AS ACTUAL DISPLAYED LABEL
            value: identifier, // used by our code to hold ID (using property label is confusing)
            disabled: isEditorDisabled || isOptionDisabled(identifier),
            order: index
        }));
    }

    const multiselectSearchFunction = (option: ObjectOption, searchText: string): boolean => {
        const acceptableValue: MODEL_VALUE = getOptionById(options.acceptableValues, getOptionIdResolver(options), option.value);
        const label: string | MODEL_VALUE = loc()(acceptableValue);
        if (isString(label)) {
            return label.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()); // acceptable option has text label
        } else {
            return option.label.toString().toLocaleLowerCase().includes(searchText.toLocaleLowerCase()); // fallback to id as label
        }
    };

    function changeOptionsOrderIfReorderable(optionsIdentifiers: (string | number)[]): (string | number)[] {
        if (!options?.reorderable) {
            return optionsIdentifiers;
        }

        const selected = optionsIdentifiers.filter((optionIdentifier) => currentViewValue.includes(optionIdentifier));
        const notSelected = optionsIdentifiers.filter((optionIdentifier) => !currentViewValue.includes(optionIdentifier));
        // sort selected options in the same order as they are in view value
        const sortedSelected = selected.sort((a, b) => currentViewValue.indexOf(a) - currentViewValue.indexOf(b));

        return [...sortedSelected, ...notSelected];
    }

    function moveUp(index: number) {
        if (index === 0) {
            return;
        }
        // eslint-disable-next-line prefer-const
        let optionIdentifiers = [...currentViewValue]; // copy
        [optionIdentifiers[index - 1], optionIdentifiers[index]] = [optionIdentifiers[index], optionIdentifiers[index - 1]]; // swap
        formControl.setViewValue(optionIdentifiers);
    }

    function moveDown(index: number) {
        if (index === currentViewValue.length - 1) {
            return;
        }
        // eslint-disable-next-line prefer-const
        let optionIdentifiers = [...currentViewValue]; // copy
        [optionIdentifiers[index], optionIdentifiers[index + 1]] = [optionIdentifiers[index + 1], optionIdentifiers[index]]; // swap
        formControl.setViewValue(optionIdentifiers);
    }

    // to align toolbars in the same distance from the left with the longest label
    // implemented in JS because CSS does not allow using 2 CSS functions in the same rule (max and calc)
    // returns dynamically calculated right margin size in px
    // while container is not yet initialized, it will use 0 as its width (negative value gets later clamped to 0)
    function getRightMarginSizeForChangeOrderToolbar(identifiers: (string | number)[], containerWidth: number | undefined = 0): number {

        // calculate width of text (in px) using canvas
        // https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript
        const getTextWidth = (text: string): number => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            context.font = getComputedStyle(document.body).font;
            return context.measureText(text).width;
        };

        const longestCheckboxLabel = Math.max(...identifiers.map((identifier) => getTextWidth(identifier.toString()))); // in px
        const REST_SIZE = 100; // estimated size of other elements in the `selectable-option-item` line (checkbox + margins/paddings)
        return containerWidth - (longestCheckboxLabel + REST_SIZE); // same effect as css rule `calc(100% - (longestCheckboxLabel + REST_SIZE))`
    }

    export function getFormController(): FormControl<MODEL_VALUE[]> {
        return formControl;
    }
</script>

{#if inlineMode}
    <button type="button" on:click={() => forceBlockMode = true} class="sr-only" data-change-to-block-mode>
        {localize('changeToCheckboxesMsg')}
    </button>

    <div class="inline-mode" class:remove-all-btn-hidden={isNoneSelectableOptionSelected}>
        <MultiSelect
            id={editorId}
            name={editorName}
            options={addMetadataForMultiSelect(selectableOptionsIdentifiers, isDisabled)}
            selected={addMetadataForMultiSelect(currentViewValue, isDisabled)}
            filterFunc={multiselectSearchFunction}
            placeholder={placeholder}
            disabled={isDisabled}
            required={validations?.required ?? false}
            defaultDisabledTitle={null}
            disabledInputTitle={null}
            noMatchingOptionsMsg={localize('allSelected')}
            removeAllTitle={localize('deselectAll')}
            removeBtnTitle={localize('deselectOption')}
            bind:open={isListboxOpen}
            bind:outerDiv={multiselectContainerRef}
            bind:input={multiselectInputRef}
            outerDivClass="multiselect-form-control input-{size}"
            liSelectedClass="selected-option-box"
            inputClass="main-input nested-input-{size}"
            ulOptionsClass="dropdown-menu size-{size} {isListboxOpen ? 'opened': ''}"
            liOptionClass="dropdown-menu-option"
            liActiveOptionClass="active"
            on:add={onAdd}
            on:remove={onRemove}
            on:removeAll={deselectAll}>

            <span slot="disabled-icon" class="hidden" aria-hidden="true">
                <!-- hide default disabled input icon -->
            </span>

            <span slot="option" let:option>
                <span class="option-label">
                    <svelte:component this={options.optionLabelComponent}
                                      option={getOptionById(options.acceptableValues, getOptionIdResolver(options), assertObjectOption(option).value)}>
                    </svelte:component>
                </span>
            </span>

            <svelte:fragment slot="selected" let:option let:idx>
                <span class="option-label" class:option-disabled={assertObjectOption(option).disabled}>
                    <svelte:component this={options.optionLabelComponent}
                                      option={getOptionById(addModelValueIfNotInOptions(options.acceptableValues), getOptionIdResolver(options), assertObjectOption(option).value)}>
                    </svelte:component>
                </span>

                {#if (options.reorderable && idx !== 0)}
                    <button type="button"
                            class="custom-button move-up-btn"
                            on:mouseup|stopPropagation
                            on:click={() => moveUp(idx)}
                            aria-label={localize('moveUp')}
                            title={localize('moveUp')}>

                        <UIcon icon="angle-small-left"/>
                    </button>
                {/if}

                {#if (options.reorderable && idx !== currentViewValue.length - 1)}
                    <button type="button"
                            class="custom-button move-down-btn"
                            on:mouseup|stopPropagation
                            on:click={() => moveDown(idx)}
                            aria-label={localize('moveDown')}
                            title={localize('moveDown')}>

                        <UIcon icon="angle-small-right"/>
                    </button>
                {/if}

                {#if assertObjectOption(option).disabled}
                    <!-- this is just marker to hide (with CSS) first button element placed after this marker in DOM -->
                    <span data-next-btn-hidden-marker aria-hidden="true"></span>
                {/if}
            </svelte:fragment>
        </MultiSelect>
    </div>
    <ErrorMessages formController={formControl} {forceShowErrors} {size}/>
{:else}
    <div class="block-mode size-{size}">
        <ul id={editorId}
            data-name={editorName}
            use:ariaLabelledby={labelContext}
            use:focusOutside
            use:touched={{formControl, touchTriggerEvent: FOCUS_OUTSIDE_EVENT_TYPE}}
            use:ariaInvalid={formControl}
            use:ariaErrormessage={formControl}
            use:ariaDescribedby={inputDescription}
            use:style={{field: formControl.getFieldStateAsStore()}}
            bind:this={checkboxListRef}
            data-main-input>

            {#each changeOptionsOrderIfReorderable(selectableOptionsIdentifiers) as selectableOptionIdentifier, index (selectableOptionIdentifier)}
                <li class="selectable-option-item"
                    class:no-line-wrap={!options.reorderable}
                    aria-setsize={options.acceptableValues.length}
                    aria-posinset={index + 1}
                    transition:slide={{duration: 250}}>

                    <Checkbox id={`${editorId}-checkbox${index}`}
                              disabled={isDisabled || isOptionDisabled(selectableOptionIdentifier)}
                              value={selectableOptionIdentifier}
                              checked={currentViewValue.includes(selectableOptionIdentifier)}
                              {size}
                              name={`${editorName}-choice`}
                              required={validations.required && (isNull(model) || model.length === 0) ? 'true' : null}
                              bind:this={checkboxRefs[index]}
                              on:change={(event) => toggleOptionCheckbox(event.detail.checked, selectableOptionIdentifier)}>
                        <svelte:component this={options.optionLabelComponent}
                                          option={getOptionById(options.acceptableValues, getOptionIdResolver(options), selectableOptionIdentifier)}>
                        </svelte:component>
                    </Checkbox>
                    {#if (options.reorderable && currentViewValue.includes(selectableOptionIdentifier))}
                        <div class="btn-group btn-group-xs change-order-toolbar"
                             style="--margin-right-size: {getRightMarginSizeForChangeOrderToolbar(selectableOptionsIdentifiers, checkboxListRef?.clientWidth)}px"
                             role="group">

                            {#if (index !== 0)}
                                <button type="button"
                                        class="btn btn-default move-up-btn"
                                        on:click={() => moveUp(index)}
                                        aria-label={localize('moveUp')}
                                        title={localize('moveUp')}>

                                    <UIcon icon="arrow-small-up"/>
                                </button>
                            {/if}
                            {#if (index !== currentViewValue.length - 1)}
                                <button type="button"
                                        class="btn btn-default move-down-btn"
                                        on:click={() => moveDown(index)}
                                        aria-label={localize('moveDown')}
                                        title={localize('moveDown')}>

                                    <UIcon icon="arrow-small-down"/>
                                </button>
                            {/if}
                        </div>
                    {/if}
                </li>
            {/each}
        </ul>

        {#if (hasMoreCheckboxes)}
            <div>
                <!-- DIV container to provide block formatting -->
                <button type="button"
                        class="btn btn-link btn-{size} expand-toggle"
                        on:click={toggleExpand}
                        aria-controls={editorId}
                        aria-expanded={isCheckboxListExpanded}
                        data-more-options-count={moreCheckboxesCount}>

                    <UIcon icon="angle-small-{isCheckboxListExpanded ? 'up' : 'down'}"/>

                    {#if (isCheckboxListExpanded)}
                        <span>{localize('less')}</span>
                    {:else}
                        <span>{`${localize('more')} (${moreCheckboxesCount})`}</span>
                    {/if}
                </button>
            </div>
        {/if}

        <div class="btn-group btn-group-{size} select-toolbar" role="group">
            <button class="btn btn-default select-all"
                    type="button"
                    disabled={isDisabled || areAllSelectableOptionSelected}
                    on:click={selectAll}>
                {localize('selectAll')}
            </button>

            <button class="btn btn-default deselect-all"
                    type="button"
                    disabled={isDisabled || isNoneSelectableOptionSelected}
                    on:click={deselectAll}>
                {localize('deselectAll')}
            </button>
        </div>
    </div>

    <ErrorMessages formController={formControl} {forceShowErrors} {size} customClass="acceptable-checkboxes" standalone={true}/>
{/if}

<Description {inputDescription}/>

<style lang="less" global>
    @import (reference) "bootstrap-less/bootstrap/variables.less";
    @import (reference) "bootstrap-less/bootstrap/mixins.less";
    @import (reference) "bootstrap-less/bootstrap/forms.less";

    .multiselect-form-control-custom-focus() {
        @color: @input-border-focus;
        @color-rgba: rgba(red(@color), green(@color), blue(@color), .6);
        border-color: @color;
        outline: 0;
        .box-shadow(~"inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px @{color-rgba}");
    }

    kp-value-editor[data-type="multiple-acceptable"] .inline-mode {

        .multiselect-form-control {
            --sms-li-disabled-bg: "transparent"; // overwrite multi-selects defaults
            --sms-open-z-index: auto; // overwrite multi-selects defaults

            .form-control();
            display: flex;
            height: fit-content;

            &.input-sm {
                .input-size(@input-height-small; @padding-small-vertical; @padding-small-horizontal; @font-size-small; @line-height-small; @input-border-radius-small);
                height: fit-content;
            }

            &.input-xs {
                .input-size(22px; 1px; 5px; @font-size-small; @line-height-small; @input-border-radius-small);
                height: fit-content;

                .selected-option-box {
                    margin: 1px;
                }
            }

            &:focus-within {
                .multiselect-form-control-custom-focus();
            }

            &.disabled {
                cursor: @cursor-disabled;
                background-color: @input-bg-disabled;
            }

            input {
                height: 1.42857143em;
                .placeholder();

                &:focus-visible {
                    outline: none !important;
                }

                &.nested-input-sm {
                    font-size: @font-size-small;
                    line-height: @line-height-small
                }

                &.nested-input-xs {
                    font-size: 12px;
                    line-height: 1.5;
                }

                &:disabled {
                    cursor: @cursor-disabled;
                    background-color: @input-bg-disabled;
                }
            }

            .option-disabled {
                opacity: 0.65;
            }

            button {
                &.custom-button { // copied from multi-selects native button to achieve same looks and behaviour
                    border-radius: 50%;
                    display: flex;
                    transition: 0.2s;
                    color: inherit;
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    outline: none;
                    margin: 0 0 0 3pt;
                }

                padding: 1px; // make buttons bigger

                &:focus, &:hover {
                    color: @input-border-focus;
                    background: rgba(0, 0, 0, 0.2);
                }
            }

            [data-next-btn-hidden-marker] + button { // any btn placed immediately after marker element will be hidden
                display: none;
            }

            .selected-option-box {
                border: 1px solid @btn-default-border;
                background-color: @btn-default-bg;
                cursor: default;
                white-space: normal;

                &:hover {
                    background-color: darken(@btn-default-bg, 10%);
                    border-color: darken(@btn-default-border, 12%);
                }
            }
        }

        &.remove-all-btn-hidden .multiselect-form-control .remove-all {
            display: none;
        }

    }

    .has-error {
        kp-value-editor[data-type="multiple-acceptable"] .inline-mode {
            .multiselect-form-control {
                border-color: @state-danger-text;
                .box-shadow(inset 0 1px 1px rgba(0, 0, 0, .075)); // Redeclare so transitions work
                &:focus-within {
                    border-color: darken(@state-danger-text, 10%);
                    @shadow: inset 0 1px 1px rgba(0, 0, 0, .075), 0 0 6px lighten(@state-danger-text, 20%);
                    .box-shadow(@shadow);
                }
            }
        }

        /* reset error styles if editor is nested in meta-editor which has error */

        .has-not-error {
            kp-value-editor[data-type="multiple-acceptable"] .inline-mode {
                .multiselect-form-control {
                    .form-control();

                    &:focus-within {
                        .multiselect-form-control-custom-focus();
                    }
                }
            }
        }
    }

    kp-value-editor[data-type="multiple-acceptable"] .block-mode {
        ul {
            padding: 0;
            margin: 0;
        }

        ul, li {
            list-style-type: none;
        }

        .selectable-option-item {
            --checkbox-max-width: 80%;

            padding-left: 6px; // padding to prevent checkbox shadow cutting on the left
            padding-right: 6px; // symmetrical
            overflow: hidden;
            text-overflow: ellipsis;
            display: flex;
            justify-content: space-between;

            &.no-line-wrap {
                white-space: nowrap;
            }
        }

        &.size-md {
            .selectable-option-item {
                padding-top: 7px;
                padding-bottom: 7px;
            }
        }

        &.size-sm,
        &.size-xs {
            .selectable-option-item {
                font-size: 12px;
            }
        }

        &.size-sm {
            .selectable-option-item {
                padding-top: 6px;
                padding-bottom: 6px;
            }
        }

        &.size-xs {
            .selectable-option-item {
                padding-top: 2px;
                padding-bottom: 2px;
            }
        }

        button.expand-toggle {
            &.btn-md, &.btn-sm {
                padding-top: 3px;
                padding-bottom: 3px;
            }
        }

        .select-toolbar {
            &.btn-group-md, &.btn-group-sm {
                button {
                    padding-top: 3px;
                    padding-bottom: 3px;
                }
            }
        }

        .change-order-toolbar {
            margin-right: ~"max(var(--margin-right-size), 0px)"; // escaped string to ignore LESS max function and use native CSS function
        }
    }

    .has-error kp-value-editor[data-type="multiple-acceptable"] .error-message.acceptable-checkboxes {
        margin-top: 5px;
    }
</style>